import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { FileText, Upload, Link2, ExternalLink, Check, X, Send, UserCheck, Undo2, ClipboardList, Download, Trash2 } from 'lucide-react';
import {
  getOffers, createOffer, transitionOffer, uploadOfferAttachmentPdf,
  getPositionBindings, getBindableSlots,
  getSalaryMaterials, uploadSalaryMaterials, deleteSalaryMaterial,
} from '../api';
import { formatBeijingTime } from '../utils/dateFormat';
import {
  Offer, OfferAction, OfferStatus, OFFER_STATUS_LABELS,
  HeadcountSlot, HC_STATUS_LABELS, HC_EMPLOYMENT_LABELS, HcEmploymentType, User,
} from '../types';
import OfferLetterModal from './OfferLetterModal';

interface Props {
  candidate: { id: string; name: string; position_id?: string | null; stage: string };
  currentUser: User;
  isHr: boolean;
  users: User[];
  onChanged: () => void;
}

/** 可选编制项：已绑定(招聘中)可直接发；未绑定(空闲)选中后自动绑定再发 */
interface SlotOption {
  slot: HeadcountSlot;
  bound: boolean;
}

const EMPLOYMENT_TYPES: HcEmploymentType[] = ['fulltime', 'intern', 'outsource', 'consultant', 'temp'];

/** 每个 Offer 状态下 HR 可执行的流转动作 */
const ACTIONS_BY_STATUS: Record<OfferStatus, { action: OfferAction; label: string; tone: string; icon: React.ReactNode }[]> = {
  pending_approval: [
    { action: 'approve', label: '审批通过', tone: 'green', icon: <Check size={13} /> },
    { action: 'reject', label: '审批拒绝', tone: 'red', icon: <X size={13} /> },
  ],
  approved: [
    { action: 'send', label: '发送候选人', tone: 'blue', icon: <Send size={13} /> },
  ],
  sent: [
    { action: 'accept', label: '候选人接受', tone: 'green', icon: <UserCheck size={13} /> },
    { action: 'decline', label: '候选人拒绝', tone: 'red', icon: <X size={13} /> },
  ],
  accepted: [
    { action: 'complete', label: '确认入职', tone: 'green', icon: <Check size={13} /> },
  ],
  rejected: [], declined: [], withdrawn: [], completed: [],
};

const TONE_CLASS: Record<string, string> = {
  green: 'text-white bg-green-600 hover:bg-green-700',
  red: 'text-red-600 bg-red-50 hover:bg-red-100',
  blue: 'text-white bg-blue-600 hover:bg-blue-700',
  gray: 'text-gray-600 bg-gray-100 hover:bg-gray-200',
};

const STATUS_BADGE: Record<OfferStatus, string> = {
  pending_approval: 'bg-amber-50 text-amber-600',
  approved: 'bg-blue-50 text-blue-600',
  rejected: 'bg-red-50 text-red-600',
  sent: 'bg-violet-50 text-violet-600',
  accepted: 'bg-teal-50 text-teal-600',
  declined: 'bg-red-50 text-red-600',
  withdrawn: 'bg-gray-100 text-gray-500',
  completed: 'bg-green-50 text-green-600',
};

export default function OfferDecisionPanel({ candidate, currentUser, isHr, users, onChanged }: Props) {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [slotOptions, setSlotOptions] = useState<SlotOption[]>([]);
  const [letterInitial, setLetterInitial] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);

  // 发起 Offer 表单
  const [slotId, setSlotId] = useState('');
  const [salary, setSalary] = useState('');
  const [employmentType, setEmploymentType] = useState<HcEmploymentType>('fulltime');
  const [onboardDate, setOnboardDate] = useState('');
  const [probation, setProbation] = useState('3');
  const [workLocation, setWorkLocation] = useState('');
  const [reason, setReason] = useState('');
  const [approverIds, setApproverIds] = useState<string[]>([]);
  const [offerImage, setOfferImage] = useState<File | null>(null);
  // 薪酬明细待填字段（来自 Offer 录用通知书）
  const [compAmount, setCompAmount] = useState('');
  const [compUnit, setCompUnit] = useState('月');
  const [compCurrency, setCompCurrency] = useState('RMB');
  const [compPretax, setCompPretax] = useState(true);
  const [internEndDate, setInternEndDate] = useState('');
  const [acceptDeadline, setAcceptDeadline] = useState('');
  const [perks, setPerks] = useState('');

  // 薪酬材料
  const [salMats, setSalMats] = useState<any[]>([]);
  const [salFiles, setSalFiles] = useState<File[]>([]);
  const [salNote, setSalNote] = useState('');
  const [salUploading, setSalUploading] = useState(false);

  // 审批/流转意见
  const [commentById, setCommentById] = useState<Record<string, string>>({});

  const positionId = candidate.position_id || '';

  const load = async () => {
    setLoading(true);
    try {
      const [ofs, mats] = await Promise.all([
        getOffers({ candidate_id: candidate.id }),
        getSalaryMaterials(candidate.id).catch(() => []),
      ]);
      setOffers(Array.isArray(ofs) ? ofs : []);
      setSalMats(Array.isArray(mats) ? mats : []);
      if (positionId) {
        const [bindings, bindable] = await Promise.all([
          getPositionBindings(positionId).catch(() => []),
          getBindableSlots(positionId).catch(() => []),
        ]);
        const opts: SlotOption[] = [];
        // 已绑定且处于招聘中的编制：可直接发 Offer
        (Array.isArray(bindings) ? bindings : [])
          .filter((b: any) => b.status === 'active' && b.slot && b.slot.status === 'RECRUITING')
          .forEach((b: any) => opts.push({ slot: b.slot, bound: true }));
        // 同部门空闲编制：选中后自动绑定再发
        (Array.isArray(bindable) ? bindable : []).forEach((s: HeadcountSlot) => opts.push({ slot: s, bound: false }));
        setSlotOptions(opts);
      } else {
        setSlotOptions([]);
      }
    } catch {
      toast.error('加载 Offer 数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidate.id, positionId]);

  const activeOffer = useMemo(() => offers.find(o => o.in_progress), [offers]);
  const canApprove = (o: Offer) =>
    currentUser.role === 'admin' ||
    (o.approver_user_ids || []).includes(currentUser.id) ||
    currentUser.role === 'hr';

  const handleCreate = async () => {
    if (!positionId) { toast.error('候选人未关联岗位，请先在候选人信息中设置岗位'); return; }
    if (!slotId) { toast.error('请选择要占用的编制'); return; }
    setBusy(true);
    try {
      await createOffer({
        candidate_id: candidate.id,
        headcount_slot_id: slotId,
        salary_annual: salary ? Number(salary) : undefined,
        employment_type: employmentType,
        expected_onboard_date: onboardDate || undefined,
        probation_months: probation ? Number(probation) : undefined,
        work_location: workLocation || undefined,
        comp_amount: compAmount ? Number(compAmount) : undefined,
        comp_unit: compUnit || undefined,
        comp_currency: compCurrency || undefined,
        comp_pretax: compPretax,
        intern_end_date: internEndDate || undefined,
        accept_deadline: acceptDeadline || undefined,
        perks: perks || undefined,
        reason: reason || undefined,
        approver_user_ids: approverIds,
        offer_image: offerImage,
      });
      toast.success('Offer 已发起，编制已锁定');
      setShowCreate(false);
      setSlotId(''); setSalary(''); setOnboardDate(''); setWorkLocation('');
      setCompAmount(''); setInternEndDate(''); setAcceptDeadline(''); setPerks('');
      setReason(''); setApproverIds([]); setOfferImage(null);
      await load();
      onChanged();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '发起 Offer 失败');
    } finally {
      setBusy(false);
    }
  };

  const handleSalaryUpload = async () => {
    if (!salFiles.length) return;
    setSalUploading(true);
    try {
      await uploadSalaryMaterials(candidate.id, salFiles, salNote || undefined);
      toast.success('薪酬材料上传成功');
      setSalFiles([]); setSalNote('');
      setSalMats(await getSalaryMaterials(candidate.id));
    } catch { toast.error('上传失败'); }
    finally { setSalUploading(false); }
  };

  const handleSalaryDelete = async (id: string) => {
    if (!window.confirm('确认删除该薪酬材料？')) return;
    try { await deleteSalaryMaterial(id); toast.success('已删除'); setSalMats(await getSalaryMaterials(candidate.id)); }
    catch { toast.error('删除失败'); }
  };

  const openLetterFor = (o: Offer) => setLetterInitial({
    candidate_name_cn: candidate.name || '',
    work_location_cn: o.work_location || '深圳',
    work_location_en: /深圳/.test(o.work_location || '') ? 'Shenzhen' : '',
    start_date: o.expected_onboard_date || '',
    intern_end_date: o.intern_end_date || '',
    allowance_amount: o.comp_amount ? String(o.comp_amount) : (o.salary_annual ? String(o.salary_annual) : ''),
    allowance_currency: o.comp_currency || 'RMB',
    allowance_unit: o.comp_unit || '',
    accept_deadline: o.accept_deadline || '',
    sign_date: new Date().toISOString().slice(0, 10),
  });

  const handleTransition = async (offer: Offer, action: OfferAction) => {
    const needReason = action === 'reject' || action === 'decline' || action === 'withdraw';
    const reasonText = commentById[offer.id] || '';
    if (needReason && !reasonText.trim() && action !== 'withdraw') {
      toast.error('请填写原因');
      return;
    }
    setBusy(true);
    try {
      await transitionOffer(offer.id, action, reasonText);
      toast.success(`${OFFER_STATUS_LABELS[offer.status]} → 已${action === 'complete' ? '确认入职' : '处理'}`);
      setCommentById(prev => ({ ...prev, [offer.id]: '' }));
      await load();
      onChanged();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '操作失败');
    } finally {
      setBusy(false);
    }
  };

  const handleUploadPdf = async (offer: Offer, file: File) => {
    setBusy(true);
    try {
      await uploadOfferAttachmentPdf(offer.id, file);
      toast.success('Offer PDF 已上传');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '上传失败');
    } finally {
      setBusy(false);
    }
  };

  const toggleApprover = (id: string) =>
    setApproverIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  if (loading) {
    return <p className="text-center text-xs text-gray-400 py-6">加载中…</p>;
  }

  return (
    <div className="space-y-4">
      {/* 头部 + 发起按钮 */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
          <span className="w-1.5 h-4 rounded-full bg-violet-500 inline-block" />
          Offer 与编制占用
          {offers.length > 0 && <span className="text-xs text-gray-400 font-normal">({offers.length})</span>}
        </h3>
        {isHr && !activeOffer && !showCreate && (
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
            <FileText size={13} />发起 Offer
          </button>
        )}
      </div>

      {/* 无岗位提示 */}
      {!positionId && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
          候选人尚未关联岗位，无法发起 Offer。请先在候选人信息中设置意向岗位。
        </div>
      )}

      {/* 薪酬材料（在 Offer 决策流程内直接上传/查看/删除） */}
      <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-2.5">
        <div className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5">
          <FileText size={13} />薪酬材料
          {salMats.length > 0 && <span className="text-gray-400 font-normal">({salMats.length})</span>}
        </div>
        {isHr && (
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
              onChange={e => setSalFiles(Array.from(e.target.files || []))}
              className="flex-1 min-w-[180px] text-xs text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-emerald-100 file:text-emerald-700 hover:file:bg-emerald-200"
            />
            <input value={salNote} onChange={e => setSalNote(e.target.value)} placeholder="备注（可选）"
              className="h-8 rounded-lg border border-gray-200 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500" />
            <button
              disabled={salFiles.length === 0 || salUploading}
              onClick={handleSalaryUpload}
              className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 rounded-lg transition-colors shrink-0">
              {salUploading ? '上传中...' : '上传'}
            </button>
            {salFiles.length > 0 && <span className="text-[11px] text-gray-500 w-full">已选 {salFiles.length} 个文件</span>}
          </div>
        )}
        {salMats.length === 0 ? (
          <div className="text-[11px] text-gray-400">暂无薪酬材料</div>
        ) : (
          <div className="space-y-1.5">
            {salMats.map((m: any) => {
              const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(m.file_name);
              const isPdf = /\.pdf$/i.test(m.file_name);
              const isExcel = /\.(xls|xlsx)$/i.test(m.file_name);
              return (
                <div key={m.id} className="bg-white rounded-lg border border-gray-200 p-2 flex items-center gap-2.5">
                  <span className={clsx('shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[9px] font-bold',
                    isPdf ? 'bg-red-50 text-red-500' : isExcel ? 'bg-green-50 text-green-600' : isImg ? 'bg-blue-50 text-blue-500' : 'bg-gray-50 text-gray-500')}>
                    {isPdf ? 'PDF' : isExcel ? 'XLS' : isImg ? 'IMG' : 'FILE'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <a href={m.file_url} target="_blank" rel="noreferrer" className="text-xs font-medium text-gray-800 hover:text-primary-600 truncate block">{m.file_name}</a>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-400">
                      <span>{m.uploader_name || '未知'}</span>
                      <span>{formatBeijingTime(m.created_at, 'date')}</span>
                      {m.note && <span>· {m.note}</span>}
                    </div>
                  </div>
                  <a href={m.file_url} target="_blank" rel="noreferrer" className="p-1 text-gray-400 hover:text-primary-600" title="下载"><Download size={13} /></a>
                  {isHr && (
                    <button onClick={() => handleSalaryDelete(m.id)} className="p-1 text-gray-300 hover:text-red-500" title="删除"><Trash2 size={13} /></button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 发起 Offer 表单 */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-700">发起 Offer（将锁定所选编制）</h4>

          {/* 编制选择 */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">占用编制 <span className="text-red-500">*</span></label>
            {slotOptions.length === 0 ? (
              <div className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
                该岗位所在部门暂无可用编制。请在<span className="font-medium text-gray-600">岗位详情 → 编制绑定</span>中确认，或联系管理员新增编制。
              </div>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {slotOptions.map(({ slot, bound }) => (
                  <label key={slot.id}
                    className={clsx('flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs',
                      slotId === slot.id ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:bg-gray-50')}>
                    <input type="radio" name="slot" checked={slotId === slot.id}
                      onChange={() => setSlotId(slot.id)} className="accent-primary-600" />
                    <span className="font-mono font-medium text-gray-700">{slot.code}</span>
                    {slot.position_name && <span className="font-medium text-gray-800">{slot.position_name}</span>}
                    <span className="text-gray-500">{slot.job_level}</span>
                    <span className="text-gray-400">{HC_EMPLOYMENT_LABELS[slot.employment_type || 'fulltime']}</span>
                    <span className={clsx('ml-auto px-1.5 py-0.5 rounded text-[10px]',
                      bound ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500')}>
                      {bound ? HC_STATUS_LABELS[slot.status] : '空闲·选中自动绑定'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">年薪 (元)</label>
              <input type="number" value={salary} onChange={e => setSalary(e.target.value)}
                placeholder="如 420000"
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">用工类型</label>
              <select value={employmentType} onChange={e => setEmploymentType(e.target.value as HcEmploymentType)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{HC_EMPLOYMENT_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">预计入职日期</label>
              <input type="date" value={onboardDate} onChange={e => setOnboardDate(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">试用期 (月)</label>
              <input type="number" value={probation} onChange={e => setProbation(e.target.value)}
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">工作地点</label>
            <input value={workLocation} onChange={e => setWorkLocation(e.target.value)}
              className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>

          {/* 薪酬明细（Offer 录用通知书待填字段，会带入生成的 Offer 附件） */}
          <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-3 space-y-3">
            <div className="text-xs font-semibold text-violet-700">薪酬明细（用于生成 Offer 附件）</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">薪酬 / 津贴金额</label>
                <input type="number" value={compAmount} onChange={e => setCompAmount(e.target.value)}
                  placeholder="如 270"
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">计薪单位</label>
                <select value={compUnit} onChange={e => setCompUnit(e.target.value)}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                  <option value="天/Day">天 / Day</option>
                  <option value="月">月 / Month</option>
                  <option value="年">年 / Year</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">币种</label>
                <input value={compCurrency} onChange={e => setCompCurrency(e.target.value)}
                  placeholder="RMB"
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">计税方式</label>
                <select value={compPretax ? '1' : '0'} onChange={e => setCompPretax(e.target.value === '1')}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                  <option value="1">税前</option>
                  <option value="0">税后</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">实习期结束日期</label>
                <input type="date" value={internEndDate} onChange={e => setInternEndDate(e.target.value)}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">接受录用截止日期</label>
                <input type="date" value={acceptDeadline} onChange={e => setAcceptDeadline(e.target.value)}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">福利说明</label>
              <input value={perks} onChange={e => setPerks(e.target.value)}
                placeholder="如：餐补、下午茶、节假日礼品"
                className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">发起理由 / 薪酬说明</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
              placeholder="薪酬构成、职级依据等说明"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
          </div>

          {/* 指定审批人 */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">指定审批人（将收到飞书通知）</label>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
              {users.map(u => (
                <button key={u.id} type="button" onClick={() => toggleApprover(u.id)}
                  className={clsx('px-2.5 py-1 rounded-full text-xs border transition-colors',
                    approverIds.includes(u.id) ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')}>
                  {u.display_name || u.username}
                </button>
              ))}
            </div>
          </div>

          {/* 方案截图 */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">方案截图（可选）</label>
            <input type="file" accept="image/*" onChange={e => setOfferImage(e.target.files?.[0] || null)}
              className="text-xs text-gray-500" />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button onClick={handleCreate} disabled={busy || !slotId}
              className="px-4 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-300 rounded-lg transition-colors">
              发起 Offer
            </button>
            <button onClick={() => setShowCreate(false)}
              className="px-4 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700">取消</button>
          </div>
        </div>
      )}

      {/* Offer 列表 */}
      {offers.length === 0 && !showCreate ? (
        <p className="text-center text-xs text-gray-400 py-6">暂无 Offer 记录</p>
      ) : (
        <div className="space-y-2.5">
          {offers.map(o => {
            const actions = ACTIONS_BY_STATUS[o.status] || [];
            const canDoApproval = o.status === 'pending_approval';
            return (
              <div key={o.id} className="bg-white rounded-xl border border-gray-200 p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-semibold text-gray-700">{o.code}</span>
                    <span className={clsx('px-2 py-0.5 rounded text-[11px] font-medium', STATUS_BADGE[o.status])}>
                      {o.status_label || OFFER_STATUS_LABELS[o.status]}
                    </span>
                    {o.slot_code && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                        <Link2 size={11} />{o.slot_code}
                        <span className="text-gray-400">({o.slot_status_label})</span>
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-400">{o.created_by_name}</span>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-500 mb-2">
                  {o.salary_annual ? <span>年薪：{o.salary_annual.toLocaleString()} 元</span> : null}
                  {o.employment_type ? <span>用工：{HC_EMPLOYMENT_LABELS[o.employment_type]}</span> : null}
                  {o.expected_onboard_date ? <span>预计入职：{o.expected_onboard_date}</span> : null}
                  {o.work_location ? <span>地点：{o.work_location}</span> : null}
                </div>

                {o.reason && <p className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2 mb-2">{o.reason}</p>}

                {/* 审批意见展示 */}
                {o.approver_comment && (
                  <p className="text-[11px] text-gray-500 mb-2">
                    审批人 {o.approver_name}：{o.approver_comment}
                  </p>
                )}

                {/* 附件 */}
                <div className="flex items-center gap-3 mb-2">
                  {o.offer_image && (
                    <a href={o.offer_image} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                      <ExternalLink size={11} />方案截图
                    </a>
                  )}
                  {o.offer_pdf && (
                    <a href={o.offer_pdf} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                      <FileText size={11} />Offer PDF
                    </a>
                  )}
                  {isHr && (
                    <button
                      onClick={() => openLetterFor(o)}
                      className="inline-flex items-center gap-1 text-[11px] text-violet-600 hover:underline">
                      <FileText size={11} />生成Offer附件
                    </button>
                  )}
                </div>

                {isHr && o.in_progress && (
                  <div className="space-y-2 pt-2 border-t border-gray-100">
                    {/* 意见输入（拒绝/撤回等需说明） */}
                    <input value={commentById[o.id] || ''}
                      onChange={e => setCommentById(prev => ({ ...prev, [o.id]: e.target.value }))}
                      placeholder="审批 / 处理意见（拒绝、撤回时建议填写）"
                      className="w-full h-8 rounded-lg border border-gray-200 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500" />

                    <div className="flex flex-wrap items-center gap-2">
                      {actions.map(a => {
                        // 审批动作需审批权限
                        if (canDoApproval && !canApprove(o)) return null;
                        return (
                          <button key={a.action} disabled={busy}
                            onClick={() => handleTransition(o, a.action)}
                            className={clsx('inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50', TONE_CLASS[a.tone])}>
                            {a.icon}{a.label}
                          </button>
                        );
                      })}

                      {/* 上传 PDF：approved 之后可传 */}
                      {(o.status === 'approved' || o.status === 'sent' || o.status === 'accepted') && (
                        <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors">
                          <Upload size={13} />{o.offer_pdf ? '重传 PDF' : '上传 PDF'}
                          <input type="file" accept="application/pdf" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadPdf(o, f); }} />
                        </label>
                      )}

                      {/* 撤回：任何进行中状态 */}
                      <button disabled={busy} onClick={() => handleTransition(o, 'withdraw')}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-red-600 rounded-lg transition-colors disabled:opacity-50">
                        <Undo2 size={13} />撤回
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="flex items-center gap-1 text-[11px] text-gray-400">
        <ClipboardList size={11} />
        Offer 通过后编制自动锁定；确认入职后编制转为「已占用」，候选人流转至「已入职」。
      </p>

      {letterInitial && (
        <OfferLetterModal initial={letterInitial} onClose={() => setLetterInitial(null)} />
      )}
    </div>
  );
}
